import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useUserStore } from "@/store/userStore";
import { useAuth } from "@/contexts/AuthContext";
import {
  User,
  School,
  GraduationCap,
  BookOpen,
  Save,
  Loader2,
  Zap,
  AlertTriangle,
  Smartphone,
  Download,
  CheckCircle2,
} from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";

const GRADE_LEVELS = [
  { id: "kindergarten", label: "Kindergarten" },
  { id: "grade-1", label: "Grade 1" },
  { id: "grade-2", label: "Grade 2" },
  { id: "grade-3", label: "Grade 3" },
  { id: "grade-4", label: "Grade 4" },
  { id: "grade-5", label: "Grade 5" },
  { id: "grade-6", label: "Grade 6" },
];

const SUBJECTS = [
  { id: "mathematics", label: "Mathematics" },
  { id: "social-studies", label: "Social Studies" },
  { id: "english-language-arts", label: "English Language Arts" },
  { id: "science", label: "Science" },
];

export default function Settings() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const profile = useUserStore((s) => s.profile);
  const credits = useUserStore((s) => s.credits);
  const updateProfile = useUserStore((s) => s.updateProfile);
  const profileLoading = useUserStore((s) => s.profileLoading);
  const { isInstallable, isInstalled, install } = usePWAInstall();

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || "");
      setSchoolName(profile.schoolName || "");
      setSelectedGrades(profile.gradeLevels || []);
      setSelectedSubjects(profile.subjects || []);
    }
  }, [profile]);

  // Track changes
  useEffect(() => {
    if (!profile) return;

    const changed =
      displayName !== (profile.displayName || "") ||
      schoolName !== (profile.schoolName || "") ||
      JSON.stringify(selectedGrades.sort()) !== JSON.stringify((profile.gradeLevels || []).sort()) ||
      JSON.stringify(selectedSubjects.sort()) !== JSON.stringify((profile.subjects || []).sort());

    setHasChanges(changed);
  }, [displayName, schoolName, selectedGrades, selectedSubjects, profile]);

  const handleGradeToggle = (gradeId: string) => {
    setSelectedGrades((prev) =>
      prev.includes(gradeId)
        ? prev.filter((g) => g !== gradeId)
        : [...prev, gradeId]
    );
  };

  const handleSubjectToggle = (subjectId: string) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((s) => s !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return;
    }

    if (selectedGrades.length === 0) {
      toast.error("Please select at least one grade level");
      return;
    }

    if (selectedSubjects.length === 0) {
      toast.error("Please select at least one subject");
      return;
    }

    setSaving(true);
    const controller = new AbortController();

    try {
      await updateProfile(
        {
          displayName: displayName.trim(),
          schoolName: schoolName.trim() || null,
          gradeLevels: selectedGrades,
          subjects: selectedSubjects,
        },
        controller.signal
      );

      toast.success("Settings saved successfully");
      setHasChanges(false);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      toast.error(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error("Failed to sign out");
    }
  };

  // Calculate trial/subscription info
  const subscriptionStatus = credits?.subscriptionStatus ?? "none";
  const daysRemaining = credits?.daysRemaining;
  const creditsBalance = credits?.creditsBalance ?? 0;
  const isTrialExpired = subscriptionStatus === "trial_expired" || subscriptionStatus === "expired";

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      {/* Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>Your current plan and usage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isTrialExpired ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Trial Expired</AlertTitle>
              <AlertDescription>
                Your free trial has ended. Upgrade to continue scanning worksheets.
              </AlertDescription>
            </Alert>
          ) : subscriptionStatus === "trial" ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Free Trial</p>
                <p className="text-sm text-muted-foreground">
                  {daysRemaining} days remaining &middot; {creditsBalance} scans left
                </p>
              </div>
              <Badge variant="secondary">Trial</Badge>
            </div>
          ) : subscriptionStatus === "active" ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Active Subscription</p>
                <p className="text-sm text-muted-foreground">
                  Unlimited scans included
                </p>
              </div>
              <Badge variant="default">Active</Badge>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">No Active Plan</p>
                <p className="text-sm text-muted-foreground">
                  {creditsBalance} scans remaining
                </p>
              </div>
              <Badge variant="outline">None</Badge>
            </div>
          )}

          <Button variant="outline" className="w-full" disabled>
            Upgrade to Pro (Coming Soon)
          </Button>
        </CardContent>
      </Card>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>Your personal information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              placeholder="e.g., Ms. Johnson"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is how you'll appear in the app
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="schoolName" className="flex items-center gap-2">
              <School className="h-4 w-4" />
              School Name
            </Label>
            <Input
              id="schoolName"
              placeholder="e.g., Westbrook Elementary"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Email Address
            </Label>
            <Input
              value={profile?.email || ""}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Contact support to change your email
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Grade Levels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Grade Levels
          </CardTitle>
          <CardDescription>Select all the grades you teach</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {GRADE_LEVELS.map((grade) => (
              <div
                key={grade.id}
                className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer"
                onClick={() => handleGradeToggle(grade.id)}
              >
                <Checkbox
                  id={grade.id}
                  checked={selectedGrades.includes(grade.id)}
                  onCheckedChange={() => handleGradeToggle(grade.id)}
                />
                <Label
                  htmlFor={grade.id}
                  className="flex-1 cursor-pointer font-normal text-sm"
                >
                  {grade.label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subjects */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Subjects
          </CardTitle>
          <CardDescription>Select all the subjects you teach</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SUBJECTS.map((subject) => (
              <div
                key={subject.id}
                className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer"
                onClick={() => handleSubjectToggle(subject.id)}
              >
                <Checkbox
                  id={subject.id}
                  checked={selectedSubjects.includes(subject.id)}
                  onCheckedChange={() => handleSubjectToggle(subject.id)}
                />
                <Label
                  htmlFor={subject.id}
                  className="flex-1 cursor-pointer font-normal"
                >
                  {subject.label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving || profileLoading}
          className="flex-1"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Separator />

      {/* Install App */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Install App
          </CardTitle>
          <CardDescription>Add QuickGrade to your home screen for quick access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isInstalled ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">App is installed</span>
            </div>
          ) : isInstallable ? (
            <Button onClick={install} className="w-full sm:w-auto">
              <Download className="mr-2 h-4 w-4" />
              Install QuickGrade
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                To install the app on your device:
              </p>
              <div className="text-sm space-y-2">
                <p><strong>Android (Chrome):</strong> Tap the menu (⋮) → "Add to Home screen" or "Install app"</p>
                <p><strong>iPhone/iPad (Safari):</strong> Tap Share (□↑) → "Add to Home Screen"</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sign Out */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>Sign out or manage your account</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={handleSignOut} className="w-full sm:w-auto">
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
